import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import BIRDS from 'vanta/dist/vanta.birds.min';
import FOG from 'vanta/dist/vanta.fog.min';
import WAVES from 'vanta/dist/vanta.waves.min';
import CLOUDS from 'vanta/dist/vanta.clouds.min';
import CLOUDS2 from 'vanta/dist/vanta.clouds2.min';
import GLOBE from 'vanta/dist/vanta.globe.min';
import NET from 'vanta/dist/vanta.net.min';
import CELLS from 'vanta/dist/vanta.cells.min';
import TRUNK from 'vanta/dist/vanta.trunk.min';
import TOPOLOGY from 'vanta/dist/vanta.topology.min';
import DOTS from 'vanta/dist/vanta.dots.min';
import RINGS from 'vanta/dist/vanta.rings.min';
import HALO from 'vanta/dist/vanta.halo.min';

export type VantaEffectType = 
  | 'birds' 
  | 'fog' 
  | 'waves' 
  | 'clouds' 
  | 'clouds2' 
  | 'globe' 
  | 'net' 
  | 'cells' 
  | 'trunk' 
  | 'topology' 
  | 'dots' 
  | 'rings' 
  | 'halo';

interface VantaOptions {
  backgroundColor?: string;
  color1?: string;
  color2?: string;
  scale?: number;
  speed?: number;
  mouseControls?: boolean;
  touchControls?: boolean;
  gyroControls?: boolean;
  minHeight?: number;
  minWidth?: number;
}

interface AnimatedBackgroundProps {
  effect: VantaEffectType;
  options?: VantaOptions;
  className?: string;
  style?: React.CSSProperties;
}

const VANTA_EFFECTS = {
  birds: BIRDS,
  fog: FOG,
  waves: WAVES,
  clouds: CLOUDS,
  clouds2: CLOUDS2,
  globe: GLOBE,
  net: NET,
  cells: CELLS,
  trunk: TRUNK,
  topology: TOPOLOGY,
  dots: DOTS,
  rings: RINGS,
  halo: HALO,
};

const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  effect,
  options = {},
  className = '',
  style = {},
}) => {
  const vantaRef = useRef<HTMLDivElement>(null);
  // Define proper type for Vanta effect instance
  interface VantaEffectInstance {
    destroy: () => void;
    setOptions: (options: VantaOptions) => void;
  }
  
  const [vantaEffect, setVantaEffect] = useState<VantaEffectInstance | null>(null);
  const vantaInstanceRef = useRef<VantaEffectInstance | null>(null);
  const isCleaningUp = useRef(false);

  useEffect(() => {
    let isMounted = true;
    isCleaningUp.current = false;

    const cleanupPreviousEffect = () => {
      if (vantaInstanceRef.current && !isCleaningUp.current) {
        isCleaningUp.current = true;
        try {
          vantaInstanceRef.current.destroy();
        } catch {
          // Error destroying previous Vanta effect (error variable removed)
        } finally {
          vantaInstanceRef.current = null;
          setVantaEffect(null);
        }
      }
    };

    // Clean up previous effect
    cleanupPreviousEffect();

    // Create new effect after a small delay to ensure cleanup is complete
    const timeoutId = setTimeout(() => {
      if (isMounted && vantaRef.current && !isCleaningUp.current) {
        const VantaEffect = VANTA_EFFECTS[effect];
        if (VantaEffect) {
          try {
            isCleaningUp.current = false;
            // 효과별 기본 옵션 설정
            const effectOptions: Record<string, unknown> = {
              el: vantaRef.current,
              THREE: THREE,
              mouseControls: true,
              touchControls: true,
              gyroControls: false,
              minHeight: 200.00,
              minWidth: 200.00,
              scale: options.scale || 1.00,
              scaleMobile: 1.00,
              backgroundColor: options.backgroundColor ? parseInt(options.backgroundColor.replace('#', '0x'), 16) : 0x0,
            };
            
            // 효과별 특별 옵션 추가
            switch (effect) {
              case 'birds':
                effectOptions.birdSize = 1.00;
                effectOptions.wingSpan = 30.00;
                effectOptions.speedLimit = 5.00;
                effectOptions.separation = 20.00;
                effectOptions.alignment = 20.00;
                effectOptions.cohesion = 20.00;
                effectOptions.quantity = 3.00;
                effectOptions.backgroundAlpha = 0.00;
                break;
              case 'dots':
                effectOptions.size = 3.00;
                effectOptions.spacing = 35.00;
                effectOptions.showLines = false;
                break;
              case 'topology':
                effectOptions.color = options.color1 ? parseInt(options.color1.replace('#', '0x'), 16) : 0xff3f81;
                break;
              case 'trunk':
                effectOptions.color = options.color1 ? parseInt(options.color1.replace('#', '0x'), 16) : 0xff3f81;
                effectOptions.chaos = 5.00;
                break;
              default:
                // 기본 color1, color2 설정
                effectOptions.color1 = options.color1 ? parseInt(options.color1.replace('#', '0x'), 16) : 0xff3f81;
                effectOptions.color2 = options.color2 ? parseInt(options.color2.replace('#', '0x'), 16) : 0xff6e40;
            }
            
            // 속도 옵션 추가
            if (options.speed !== undefined) {
              effectOptions.speed = options.speed;
            }
            
            const instance = VantaEffect(effectOptions);
            if (isMounted) {
              vantaInstanceRef.current = instance;
              setVantaEffect(instance);
            } else {
              // Component unmounted, clean up immediately
              instance.destroy();
            }
          } catch {
            // Failed to initialize Vanta effect (error variable removed)
          }
        }
      }
    }, 50); // Small delay to ensure proper cleanup

    // Cleanup function
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      cleanupPreviousEffect();
    };
  }, [effect, options]); // Re-create when effect or options change

  // Separate useEffect for updating options to avoid recreating the effect
  useEffect(() => {
    if (vantaEffect && vantaInstanceRef.current && !isCleaningUp.current) {
      try {
        vantaInstanceRef.current.setOptions({
          backgroundColor: options.backgroundColor ? parseInt(options.backgroundColor.replace('#', '0x'), 16) : 0x0,
          color1: options.color1 ? parseInt(options.color1.replace('#', '0x'), 16) : 0xff3f81,
          color2: options.color2 ? parseInt(options.color2.replace('#', '0x'), 16) : 0xff6e40,
          scale: options.scale || 1.0,
          speed: options.speed || 1.0,
        });
      } catch {
        // Failed to update Vanta options (error variable removed)
      }
    }
  }, [vantaEffect, options.backgroundColor, options.color1, options.color2, options.scale, options.speed]);

  return (
    <div 
      ref={vantaRef}
      className={`animated-background ${className}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        ...style,
      }}
    />
  );
};

export { AnimatedBackground };

// Preset configurations for each effect
// Commented out to fix fast refresh warning - uncomment if needed
/*
export const VANTA_PRESETS: Record<VantaEffectType, VantaOptions> = {
  birds: {
    backgroundColor: 0x0,
    color1: 0xff3f81,
    color2: 0xff6e40,
    scale: 1.00,
    speed: 1.00,
  },
  fog: {
    backgroundColor: 0x0,
    color: 0xffffff,
    speed: 1.00,
  },
  waves: {
    color: 0x2a5a8a,
    scale: 1.00,
    speed: 1.00,
  },
  clouds: {
    backgroundColor: 0x0,
    speed: 1.00,
  },
  clouds2: {
    backgroundColor: 0x0,
    scale: 1.00,
    speed: 1.00,
  },
  globe: {
    backgroundColor: 0x0,
    color: 0xff3f81,
    color2: 0xff6e40,
    scale: 1.00,
  },
  net: {
    backgroundColor: 0x0,
    color: 0xff3f81,
    scale: 1.00,
    speed: 1.00,
  },
  cells: {
    color1: 0xff3f81,
    color2: 0x303030,
    scale: 1.00,
    speed: 1.00,
  },
  trunk: {
    backgroundColor: 0x0,
    color: 0xff3f81,
    scale: 1.00,
  },
  topology: {
    backgroundColor: 0x0,
    color: 0xff3f81,
    scale: 1.00,
  },
  dots: {
    backgroundColor: 0x0,
    color: 0xff3f81,
    color2: 0xff6e40,
    scale: 1.00,
    speed: 1.00,
  },
  rings: {
    backgroundColor: 0x0,
    color: 0xff3f81,
    scale: 1.00,
  },
  halo: {
    backgroundColor: 0x0,
    scale: 1.00,
  },
};
*/